import {
  CheckIcon,
  Crosshair2Icon,
  EnvelopeClosedIcon,
  MinusIcon,
  PersonIcon,
  PlusIcon,
  ReloadIcon,
} from "@radix-ui/react-icons";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import Indicator from "src/components/Indicator";
import Loading from "src/components/Loading";
import { MapDrawer } from "src/components/RouteMap";
import SessionIdList from "src/components/SessionIdList";
import DrinkLog from "src/components/drink-log/DrinkLog";
import { Button } from "src/components/ui/button";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "src/components/ui/tabs";
import UserAvatar from "src/components/userAvatar";
import getLogsFromAlcoholData from "src/lib/getLogsFromAlcoholData";
import useFriendRequest from "src/lib/hooks/useFriendRequest";
import { Friend, FriendBase } from "src/lib/slices/userSlice";
import useSWR from "swr";

const fetcher = (token) => (url: string) =>
  fetch(url, {
    headers: {
      Authorization: token,
    },
  }).then((res) => res.json());

const FriendRequestButtons = ({
  friendRequestLoading,
  friendData,
  sendFriendRequest,
  handleRemoveFriend,
  removeFriendClicked,
  removeFriend,
}: {
  friendRequestLoading: boolean;
  friendData: FriendBase;
  sendFriendRequest: () => void;
  handleRemoveFriend: () => void;
  removeFriendClicked: boolean;
  removeFriend: () => void;
}) => {
  return (
    <>
      {friendRequestLoading && (
        <Button
          size="default"
          disabled
          variant="outline"
          style={{ fontWeight: "inherit" }}
        >
          <PersonIcon />
          <ReloadIcon style={{ marginRight: "0.5rem" }} />
          Načítání
        </Button>
      )}
      {!friendRequestLoading && (
        <>
          {(!friendData?.friendRequestSent ||
            friendData.friendRequestReceived) && (
            <>
              {friendData?.isFriend === false && (
                <Button
                  size="default"
                  onClick={sendFriendRequest}
                  style={{ fontWeight: "inherit" }}
                >
                  <PersonIcon />
                  {friendData?.friendRequestReceived ? (
                    <>
                      <CheckIcon style={{ marginRight: "0.5rem" }} />
                      Přijmout
                    </>
                  ) : (
                    <>
                      <PlusIcon style={{ marginRight: "0.5rem" }} />
                      Přidat
                    </>
                  )}
                </Button>
              )}
            </>
          )}
          {friendData?.isFriend === true && (
            <Button
              size="icon"
              onClick={handleRemoveFriend}
              variant={removeFriendClicked ? "destructive" : "outline"}
              style={{ fontWeight: "inherit" }}
            >
              <PersonIcon />
              <MinusIcon />
            </Button>
          )}
          {!friendData?.isFriend && (
            <>
              {friendData?.friendRequestSent && (
                <Button
                  size="default"
                  variant="outline"
                  onClick={removeFriend}
                  style={{ fontWeight: "inherit" }}
                >
                  <PersonIcon />
                  <EnvelopeClosedIcon style={{ marginRight: "0.5rem" }} />
                  Zažádáno
                </Button>
              )}
            </>
          )}
        </>
      )}
    </>
  );
};

export default function User() {
  const userId = useParams().userId;

  const user = useSelector((state: any) => state.user);

  const { data, error, isLoading, mutate } = useSWR<Friend>(
    `${process.env.REACT_APP_API_URL}/users/by-id/${userId}`,
    fetcher(user.token)
  );

  const [friendRequestLoading, setFriendRequestLoading] = useState(false);

  const { friendData, sendFriendRequest, removeFriend } = useFriendRequest({
    userData: data,
    setFriendRequestLoading,
    mutate,
  });

  const [removeFriendClicked, setRemoveFriendClicked] = useState(false);
  const handleRemoveFriend = () => {
    if (!removeFriendClicked) setRemoveFriendClicked(true);
    else {
      setRemoveFriendClicked(false);
      removeFriend();
    }
  };
  useEffect(() => {
    if (removeFriendClicked) {
      const timeout = setTimeout(() => setRemoveFriendClicked(false), 1000);
      return () => clearTimeout(timeout);
    }
  }, [removeFriendClicked]);

  return (
    <div className="relative">
      <div className="px-4 flex gap-2">
        <UserAvatar friend={data} />
        {data && (
          <div>
            <h2
              className="text-base font-bold"
              style={{
                color: !data?.name ? "hsl(var(--muted-foreground))" : undefined,
              }}
            >
              {data?.name ?? "Beze jména"}
            </h2>
            {data?.tag && (
              <div
                className="text-muted-foreground text-sm"
                style={{ lineHeight: "0.875rem" }}
              >
                @{data.tag}
              </div>
            )}
          </div>
        )}
      </div>
      <div className="absolute top-0 right-4">
        <FriendRequestButtons
          friendRequestLoading={friendRequestLoading}
          friendData={friendData}
          sendFriendRequest={sendFriendRequest}
          handleRemoveFriend={handleRemoveFriend}
          removeFriendClicked={removeFriendClicked}
          removeFriend={removeFriend}
        />
      </div>

      {isLoading && <Loading className="pt-4" />}
      {error && !isLoading && <div className="p-4">Chyba při načítání</div>}

      {friendData?.isFriend && data?.isFriend && (
        <div className="p-4" style={{ width: "100%" }}>
          <div className="flex items-center gap-1">
            <div style={{ height: "3rem", margin: 0 }}>
              <Indicator
                doNotDisplayText
                value={data.permille}
                maxValue={data.maxPermille}
              />
            </div>
            <div style={{ margin: 0, height: "fit-content" }}>
              <span style={{ fontWeight: 500 }}>{data.permille}</span> ‰
            </div>
          </div>
          <div className="flex gap-2 items-center pt-2">
            <MapDrawer
              asChild
              logs={getLogsFromAlcoholData({
                DrunkDrinks: data?.alcoholData?.DrunkDrinks,
                events: data?.alcoholData?.events,
                photos: data?.alcoholData?.photos,
              })}
              route={data?.alcoholData?.route}
            >
              <Button variant="outline" className="flex items-center gap-2">
                <Crosshair2Icon />
                Mapa
              </Button>
            </MapDrawer>
            {/* <StatsDrawer
              chartData={data?.permilleChartData}
              permille={data.permille}
              maxPermille={data.maxPermille}
              asChild
            >
              <Button variant="outline" className="flex items-center gap-2">
                <BarChartIcon />
                Statistiky
              </Button>
            </StatsDrawer> */}
          </div>
          <Tabs defaultValue="drunk-drinks" className="pt-8">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="drunk-drinks">
                Záznamník (
                {
                  getLogsFromAlcoholData({
                    DrunkDrinks: data?.alcoholData?.DrunkDrinks,
                    events: data?.alcoholData?.events,
                    photos: data?.alcoholData?.photos,
                  }).length
                }
                )
              </TabsTrigger>
              <TabsTrigger value="sessions">
                Seance ({data?.publicSessionIds?.length})
              </TabsTrigger>
            </TabsList>
            <TabsContent value="drunk-drinks" className="pt-2">
              <DrinkLog
                alcoholData={data?.alcoholData}
                style={{ padding: "0" }}
              />
            </TabsContent>
            <TabsContent value="sessions" className="pt-2">
              <SessionIdList sessionIds={data?.publicSessionIds} />
            </TabsContent>
          </Tabs>
        </div>
      )}
    </div>
  );
}
